import api from "./api";
import {
  type NotificationInboundSetup,
  type TeamsSettingsRequest,
  type TeamsSettingsSummary,
  downloadTeamsPackageFrom,
} from "./notificationService";
import type { PromptShortcut } from "./promptService";

/**
 * StrikeUp staff management of ONE CustomerCompany's own resources — knowledge base, skills,
 * workflows, prompt shortcuts, integrations and member roles.
 *
 * This is the admin-app twin of `api-agents.widget/src/services/portalService.ts`: the same
 * capabilities a Customer Admin has inside the widget's Customer Portal, exposed to staff on the
 * CRM company page. It is kept separate from `customerCompanyService` (which owns the
 * CustomerCompany record itself — list, create, settings, members list, account memory) so neither
 * file grows past the point of being readable.
 *
 * Every route is `/customer-companies/{customerCompanyId}/…` and authenticates with the normal
 * staff token; the server takes the tenant from that token and validates the CustomerCompany id
 * against it before use.
 */

// ── Knowledge base ───────────────────────────────────────────────────────────

export type CustomerCompanyKbDocumentStatus =
  | "Pending"
  | "Processing"
  | "Indexed"
  | "Failed"
  | "Deleted";

export interface CustomerCompanyKbDocument {
  id: string;
  fileName: string;
  status: CustomerCompanyKbDocumentStatus;
  contentType?: string | null;
  fileSizeBytes?: number | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerCompanyKbUploadUrlRequest {
  fileName: string;
  contentType: string;
  /** Lets the API refuse a file over the storage quota before it is uploaded. */
  fileSizeBytes?: number;
}

export interface CustomerCompanyKbUploadUrlResponse {
  docId: string;
  presignedUrl: string;
  s3Key: string;
  expiresIn: number;
}

export interface CustomerCompanyKbConfirmUploadRequest {
  docId: string;
  fileName: string;
  contentType: string;
  s3Key: string;
}

export interface CustomerCompanyKbRetrievedChunk {
  content: string;
  score: number;
  sourceFileName?: string | null;
}

export interface CustomerCompanyKbTestRetrievalResponse {
  success: boolean;
  errorMessage?: string | null;
  chunks: CustomerCompanyKbRetrievedChunk[];
}

// ── Skills ───────────────────────────────────────────────────────────────────

export interface CustomerCompanySkill {
  id: string;
  name: string;
  category?: string | null;
  humanReadableDescription: string;
  triggerContext: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomerCompanySkillRequest {
  name: string;
  category?: string | null;
  humanReadableDescription: string;
  triggerContext: string;
  isActive?: boolean;
}

export interface UpdateCustomerCompanySkillRequest {
  name?: string;
  category?: string | null;
  humanReadableDescription?: string;
  triggerContext?: string;
  isActive?: boolean;
}

// ── Workflows ────────────────────────────────────────────────────────────────

export type WorkflowFrequencyType = "Daily" | "Weekdays" | "Weekly" | "Monthly";

/**
 * `Cancelled` means the schedule fired while the workflow was inactive, so the run was skipped.
 * `WaitingForApproval` means the run parked tool calls for a human and has not finished.
 */
export type WorkflowExecutionStatus =
  | "Pending"
  | "Running"
  | "Completed"
  | "Failed"
  | "Cancelled"
  | "WaitingForApproval";

export interface CustomerCompanyWorkflowSchedule {
  id: string;
  workflowId: string;
  frequencyType: WorkflowFrequencyType;
  /** "HH:MM:SS" */
  timeOfDay: string;
  dayOfWeek: number | null;
  dayOfMonth: number | null;
  timezone: string;
  isActive: boolean;
  eventBridgeScheduleName: string | null;
  nextRunAt: string | null;
}

export interface CustomerCompanyWorkflowLog {
  id: string;
  workflowId: string;
  workflowScheduleId: string | null;
  chatSessionId: string | null;
  status: WorkflowExecutionStatus;
  startedAt: string;
  completedAt: string | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface CustomerCompanyWorkflow {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  schedules: CustomerCompanyWorkflowSchedule[];
  /** External customer id that owns this workflow. */
  ownerExternalId: string;
  /** Null when the owner is no longer a member of this company (an orphaned workflow). */
  ownerMemberId: string | null;
  ownerName: string | null;
  ownerEmail: string | null;
  ownerIsDisabled: boolean;
  /** True when the row was matched via its owner rather than a stamped CustomerCompanyId. */
  isLegacyOwnership: boolean;
  /** Personal: the owner's alone. Company: shared with every member, runs notify them all. */
  scope: "Personal" | "Company";
}

export interface UpdateCustomerCompanyWorkflowRequest {
  name?: string;
  description?: string;
  isActive?: boolean;
}

// ── Integrations (notification channels: Smtp, Telegram, Slack, Teams) ───────

export type CustomerCompanyNotificationProvider =
  | "Smtp"
  | "Telegram"
  | "Slack"
  | "Teams";

export const CUSTOMER_COMPANY_NOTIFICATION_PROVIDERS: CustomerCompanyNotificationProvider[] =
  ["Smtp", "Telegram", "Slack", "Teams"];

export interface CustomerCompanySmtpSettingsSummary {
  host: string;
  port: number;
  enableSsl: boolean;
  username?: string | null;
  fromEmail?: string | null;
  displayName?: string | null;
}

export interface CustomerCompanySmtpSettingsRequest {
  host: string;
  port: number;
  enableSsl: boolean;
  username?: string | null;
  /** Omit on update to keep the stored password. */
  password?: string | null;
  fromEmail?: string | null;
  displayName?: string | null;
}

/** Telegram settings as returned — the bot token never comes back. */
export interface CustomerCompanyTelegramSettingsSummary {
  /** Bot handle without the leading '@'; empty until the bot has been reached. */
  botUsername: string;
  hasBotToken: boolean;
}

export interface CustomerCompanyTelegramSettingsRequest {
  /** Omit on update to keep the stored token. */
  botToken?: string | null;
  /** Optional — read from the bot itself when left blank. */
  botUsername?: string | null;
}

/** Slack settings as returned — the bot token and signing secret never come back. */
export interface CustomerCompanySlackSettingsSummary {
  hasBotToken: boolean;
  /** A signing secret is stored; needed for buttons, replies and commands. */
  hasSigningSecret: boolean;
}

export interface CustomerCompanySlackSettingsRequest {
  /** Omit on update to keep the stored token. */
  botToken?: string | null;
  /** Omit on update to keep the stored signing secret. */
  signingSecret?: string | null;
}

export interface CustomerCompanyNotificationChannel {
  id: string;
  name: string;
  providerType: CustomerCompanyNotificationProvider;
  status: string;
  isActive: boolean;
  /** Set only when providerType is "Smtp". */
  smtpSettings?: CustomerCompanySmtpSettingsSummary | null;
  /** Set only when providerType is "Telegram". */
  telegramSettings?: CustomerCompanyTelegramSettingsSummary | null;
  /** Set only when providerType is "Slack". */
  slackSettings?: CustomerCompanySlackSettingsSummary | null;
  /** Set only when providerType is "Teams". */
  teamsSettings?: TeamsSettingsSummary | null;
  /** Set for Telegram, Slack and Teams channels. */
  inboundSetup?: NotificationInboundSetup | null;
}

export interface CreateCustomerCompanyNotificationChannelRequest {
  name: string;
  providerType: CustomerCompanyNotificationProvider;
  smtpSettings?: CustomerCompanySmtpSettingsRequest;
  telegramSettings?: CustomerCompanyTelegramSettingsRequest;
  slackSettings?: CustomerCompanySlackSettingsRequest;
  teamsSettings?: TeamsSettingsRequest;
}

export interface UpdateCustomerCompanyNotificationChannelRequest {
  name?: string;
  smtpSettings?: CustomerCompanySmtpSettingsRequest;
  telegramSettings?: CustomerCompanyTelegramSettingsRequest;
  slackSettings?: CustomerCompanySlackSettingsRequest;
  teamsSettings?: TeamsSettingsRequest;
}

export interface CustomerCompanyNotificationTestResult {
  success: boolean;
  error?: string | null;
}

// ── Members ──────────────────────────────────────────────────────────────────

export type CustomerCompanyMemberRole = "Admin" | "Member";

export interface CustomerCompanyMemberDetail {
  id: string;
  customerId: string;
  customerName?: string | null;
  customerEmail?: string | null;
  role: CustomerCompanyMemberRole;
  isDisabled: boolean;
  credentialSetAt?: string | null;
  createdAt: string;
}

const base = (customerCompanyId: string) =>
  `/customer-companies/${customerCompanyId}`;

export const customerCompanyAdminService = {
  // ── Knowledge base ─────────────────────────────────────────────────────────

  listKbDocuments: async (
    customerCompanyId: string,
  ): Promise<CustomerCompanyKbDocument[]> => {
    const res = await api.get(`${base(customerCompanyId)}/kb/documents`);
    return res.data;
  },

  getKbUploadUrl: async (
    customerCompanyId: string,
    request: CustomerCompanyKbUploadUrlRequest,
  ): Promise<CustomerCompanyKbUploadUrlResponse> => {
    const res = await api.post(
      `${base(customerCompanyId)}/kb/documents/upload-url`,
      request,
    );
    return res.data;
  },

  confirmKbUpload: async (
    customerCompanyId: string,
    request: CustomerCompanyKbConfirmUploadRequest,
  ): Promise<CustomerCompanyKbDocument> => {
    const res = await api.post(
      `${base(customerCompanyId)}/kb/documents/confirm`,
      request,
    );
    return res.data;
  },

  deleteKbDocument: async (
    customerCompanyId: string,
    documentId: string,
  ): Promise<void> => {
    await api.delete(`${base(customerCompanyId)}/kb/documents/${documentId}`);
  },

  testKbRetrieval: async (
    customerCompanyId: string,
    query: string,
    topK = 5,
  ): Promise<CustomerCompanyKbTestRetrievalResponse> => {
    const res = await api.post(`${base(customerCompanyId)}/kb/test-retrieval`, {
      query,
      topK,
    });
    return res.data;
  },

  // ── Skills ─────────────────────────────────────────────────────────────────

  listSkills: async (
    customerCompanyId: string,
  ): Promise<CustomerCompanySkill[]> => {
    const res = await api.get(`${base(customerCompanyId)}/skills`);
    return res.data;
  },

  createSkill: async (
    customerCompanyId: string,
    request: CreateCustomerCompanySkillRequest,
  ): Promise<CustomerCompanySkill> => {
    const res = await api.post(`${base(customerCompanyId)}/skills`, request);
    return res.data;
  },

  updateSkill: async (
    customerCompanyId: string,
    skillId: string,
    request: UpdateCustomerCompanySkillRequest,
  ): Promise<CustomerCompanySkill> => {
    const res = await api.put(
      `${base(customerCompanyId)}/skills/${skillId}`,
      request,
    );
    return res.data;
  },

  deleteSkill: async (
    customerCompanyId: string,
    skillId: string,
  ): Promise<void> => {
    await api.delete(`${base(customerCompanyId)}/skills/${skillId}`);
  },

  // ── Workflows ──────────────────────────────────────────────────────────────

  listWorkflows: async (
    customerCompanyId: string,
  ): Promise<CustomerCompanyWorkflow[]> => {
    const res = await api.get(`${base(customerCompanyId)}/workflows`);
    return res.data;
  },

  updateWorkflow: async (
    customerCompanyId: string,
    workflowId: string,
    request: UpdateCustomerCompanyWorkflowRequest,
  ): Promise<CustomerCompanyWorkflow> => {
    const res = await api.put(
      `${base(customerCompanyId)}/workflows/${workflowId}`,
      request,
    );
    return res.data;
  },

  deleteWorkflow: async (
    customerCompanyId: string,
    workflowId: string,
  ): Promise<void> => {
    await api.delete(`${base(customerCompanyId)}/workflows/${workflowId}`);
  },

  deleteWorkflowSchedule: async (
    customerCompanyId: string,
    workflowId: string,
    scheduleId: string,
  ): Promise<void> => {
    await api.delete(
      `${base(customerCompanyId)}/workflows/${workflowId}/schedules/${scheduleId}`,
    );
  },

  getWorkflowLogs: async (
    customerCompanyId: string,
    workflowId: string,
    limit = 20,
  ): Promise<CustomerCompanyWorkflowLog[]> => {
    const res = await api.get(
      `${base(customerCompanyId)}/workflows/${workflowId}/logs`,
      { params: { limit } },
    );
    return res.data;
  },

  // ── Prompt shortcuts ───────────────────────────────────────────────────────

  listPromptShortcuts: async (
    customerCompanyId: string,
  ): Promise<PromptShortcut[]> => {
    const res = await api.get(`${base(customerCompanyId)}/prompt-shortcuts`);
    return res.data;
  },

  createPromptShortcut: async (
    customerCompanyId: string,
    request: {
      name: string;
      description?: string;
      body: string;
      icon?: string;
      sortOrder?: number;
      isActive?: boolean;
    },
  ): Promise<PromptShortcut> => {
    const res = await api.post(
      `${base(customerCompanyId)}/prompt-shortcuts`,
      request,
    );
    return res.data;
  },

  updatePromptShortcut: async (
    customerCompanyId: string,
    promptId: string,
    request: {
      name?: string;
      description?: string;
      body?: string;
      icon?: string;
      sortOrder?: number;
      isActive?: boolean;
    },
  ): Promise<PromptShortcut> => {
    const res = await api.put(
      `${base(customerCompanyId)}/prompt-shortcuts/${promptId}`,
      request,
    );
    return res.data;
  },

  togglePromptShortcutStatus: async (
    customerCompanyId: string,
    promptId: string,
    isActive: boolean,
  ): Promise<PromptShortcut> => {
    const res = await api.patch(
      `${base(customerCompanyId)}/prompt-shortcuts/${promptId}/status`,
      { isActive },
    );
    return res.data;
  },

  deletePromptShortcut: async (
    customerCompanyId: string,
    promptId: string,
  ): Promise<void> => {
    await api.delete(`${base(customerCompanyId)}/prompt-shortcuts/${promptId}`);
  },

  reorderPromptShortcuts: async (
    customerCompanyId: string,
    items: { id: string; sortOrder: number }[],
  ): Promise<PromptShortcut[]> => {
    const res = await api.post(
      `${base(customerCompanyId)}/prompt-shortcuts/reorder`,
      { items },
    );
    return res.data;
  },

  // ── Integrations ───────────────────────────────────────────────────────────

  /** Every channel this CustomerCompany has — at most one per provider. */
  listNotificationChannels: async (
    customerCompanyId: string,
  ): Promise<CustomerCompanyNotificationChannel[]> => {
    const res = await api.get(
      `${base(customerCompanyId)}/notification-channels`,
    );
    return Array.isArray(res.data) ? res.data : [];
  },

  createNotificationChannel: async (
    customerCompanyId: string,
    request: CreateCustomerCompanyNotificationChannelRequest,
  ): Promise<CustomerCompanyNotificationChannel> => {
    const res = await api.post(
      `${base(customerCompanyId)}/notification-channels`,
      request,
    );
    return res.data;
  },

  updateNotificationChannel: async (
    customerCompanyId: string,
    channelId: string,
    request: UpdateCustomerCompanyNotificationChannelRequest,
  ): Promise<CustomerCompanyNotificationChannel> => {
    const res = await api.put(
      `${base(customerCompanyId)}/notification-channels/${channelId}`,
      request,
    );
    return res.data;
  },

  deleteNotificationChannel: async (
    customerCompanyId: string,
    channelId: string,
  ): Promise<void> => {
    await api.delete(
      `${base(customerCompanyId)}/notification-channels/${channelId}`,
    );
  },

  toggleNotificationChannelActive: async (
    customerCompanyId: string,
    channelId: string,
  ): Promise<CustomerCompanyNotificationChannel> => {
    const res = await api.post(
      `${base(customerCompanyId)}/notification-channels/${channelId}/toggle-active`,
    );
    return res.data;
  },

  testNotificationChannel: async (
    customerCompanyId: string,
    channelId: string,
    testEmail?: string,
  ): Promise<CustomerCompanyNotificationTestResult> => {
    const res = await api.post(
      `${base(customerCompanyId)}/notification-channels/${channelId}/test`,
      { testEmail: testEmail ?? null },
    );
    return res.data;
  },

  /** Downloads a Teams channel's app package (manifest and icons) for the Teams admin center. */
  downloadTeamsPackage: (
    customerCompanyId: string,
    channelId: string,
  ): Promise<void> =>
    downloadTeamsPackageFrom(
      `${base(customerCompanyId)}/notification-channels/${channelId}/teams-package`,
    ),

  // ── Members ────────────────────────────────────────────────────────────────

  changeMemberRole: async (
    customerCompanyId: string,
    memberId: string,
    role: CustomerCompanyMemberRole,
  ): Promise<CustomerCompanyMemberDetail> => {
    const res = await api.put(
      `${base(customerCompanyId)}/members/${memberId}/role`,
      { role },
    );
    return res.data;
  },

  setMemberDisabled: async (
    customerCompanyId: string,
    memberId: string,
    disabled: boolean,
  ): Promise<CustomerCompanyMemberDetail> => {
    const res = await api.post(
      `${base(customerCompanyId)}/members/${memberId}/${disabled ? "disable" : "enable"}`,
    );
    return res.data;
  },
};

export default customerCompanyAdminService;
